import { Link, useLocation } from "react-router-dom";
import { useRoleTheme } from "@/hooks/use-role-theme";
import {
  LayoutDashboard, Settings,
  Send, Heart, Target, UserCircle, Coins,
  Video, Users, Zap, BookOpen,
} from "lucide-react";
import { isReleaseFlagOn } from "@/config/featureFlags";
import { cn } from "@/lib/utils";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { AccountSwitcher } from "@/components/common/AccountSwitcher";
import { OFFERING_TYPE_THEME } from "@/lib/offeringTypeTheme";

interface NavItem {
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
  iconColor: string;
  tourId?: string;
}

const baseNavItems: NavItem[] = [

  { label: "Dashboard",     icon: LayoutDashboard, href: "/student-dashboard",   iconColor: "text-indigo-500"    },
  { label: "My Profile",    icon: UserCircle,      href: "/my-profile",          iconColor: "text-sidebar-foreground/70" },
  { label: "Find One-to-One Teachers", icon: Video,       href: "/search/one-to-one",   iconColor: OFFERING_TYPE_THEME.ONE_TO_ONE.accent },
  { label: "Find Group Classes",   icon: Users,           href: "/search/group",        iconColor: OFFERING_TYPE_THEME.GROUP.accent },
  { label: "Find Crash Courses",   icon: Zap,             href: "/search/crash-course", iconColor: OFFERING_TYPE_THEME.CRASH_COURSE.accent },
  { label: "My Inquiries",         icon: Send,            href: "/my-inquiries",        iconColor: "text-amber-500"   },
  { label: "BMC Coins",            icon: Coins,           href: "/student-coins",       iconColor: "text-indigo-500"    },

];

const learningNavItems: NavItem[] = [
  { label: "My Goal",         icon: Target, href: "/my-goal", iconColor: "text-emerald-500" },
  { label: "Saved Teachers",  icon: Heart,  href: "/saved",   iconColor: "text-rose-500"    },
];

interface StudentSidebarProps {
  studentName: string;
  studentEmail?: string;
  studentAvatar: string;
  unreadInquiries?: number;
  /** Called when a nav item is clicked (optional; used by shells that track view state) */
  onNavClick?: () => void;
}

export function StudentSidebar({ studentName, studentAvatar, unreadInquiries = 0, onNavClick }: StudentSidebarProps) {
  const location = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  useRoleTheme("student");

  const classManagementOn = isReleaseFlagOn("CLASS_MANAGEMENT");

  const mainNavItems: NavItem[] = [
    ...baseNavItems,
    ...(classManagementOn
      ? [{ label: "My Classes", icon: BookOpen, href: "/my-classes", iconColor: "text-amber-500" }]
      : []),
  ].map(item =>
    item.href === "/my-inquiries" && unreadInquiries > 0
      ? { ...item, badge: unreadInquiries }
      : item
  );

  const NavItem = ({ item }: { item: NavItem }) => {
    const isActive = location.pathname === item.href;
    const Icon = item.icon;

    return (
      <SidebarMenuItem className="list-none">
        <Link
          to={item.href}
          title={isCollapsed ? item.label : undefined}
          onClick={onNavClick}
          data-tour={item.tourId}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150",
            isActive
              ? "bg-white/[0.12] text-white shadow-sm ring-1 ring-white/10"
              : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            isCollapsed && "justify-center px-2.5"
          )}
        >
          <Icon className={cn("shrink-0", isActive ? "text-white" : item.iconColor)} style={{ width: "18px", height: "18px" }} />
          {!isCollapsed && <span className="flex-1 truncate">{item.label}</span>}
          {!isCollapsed && item.badge && (
            <span className={cn(
              "text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center",
              isActive ? "bg-white/20 text-white" : "bg-white/20 text-white"
            )}>
              {item.badge}
            </span>
          )}
        </Link>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarContent className="px-3 py-4 flex flex-col gap-4">
        {/* Profile */}
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <AccountSwitcher currentRole="STUDENT" name={studentName} avatar={studentAvatar} />
          </div>
        </div>

        {/* Main nav */}
        <div className="flex flex-col gap-1">
          <div className={cn("flex items-center px-3 mb-1", isCollapsed ? "justify-center" : "justify-between")}>
            {!isCollapsed && (
              <p className="text-xs font-bold uppercase tracking-widest text-sidebar-foreground/40">Menu</p>
            )}
            {/* Collapse/expand toggle — desktop only; on mobile the sidebar is an
                off-canvas sheet and the opener lives in DashboardTopNav. */}
            <SidebarTrigger className="hidden md:flex h-6 w-6 shrink-0" />
          </div>
          <SidebarMenu className="gap-0.5">
            {mainNavItems.map(item => <NavItem key={item.href} item={item} />)}
          </SidebarMenu>
        </div>

        {/* Learning nav */}
        <div className="flex flex-col gap-1">
          {!isCollapsed && (
            <p className="text-xs font-bold uppercase tracking-widest text-sidebar-foreground/40 px-3 mb-1">Learning</p>
          )}
          <SidebarMenu className="gap-0.5">
            {learningNavItems.map(item => <NavItem key={item.href} item={item} />)}
          </SidebarMenu>
        </div>
      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="px-3 py-3 border-t border-sidebar-border">
        <SidebarMenu className="gap-0.5">
          <SidebarMenuItem className="list-none">
            <Link
              to="/settings"
              title={isCollapsed ? "Settings" : undefined}
              onClick={onNavClick}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150",
                "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                isCollapsed && "justify-center px-2.5"
              )}
            >
              <Settings style={{ width: "18px", height: "18px" }} className="shrink-0" />
              {!isCollapsed && <span>Settings</span>}
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
